// Ops · Payments & COD: rider cash, remittances, open e-wallet orders and the payment log
// (docs/PHASE7-CONTRACT.md, contract.d.ts "Ops · Payments & COD"). Pure: node --test runs it.
import { cashOnHand, overCashLimit } from '@popcart/core/v1';
import { peso } from '../../lib/format.js';
import { parsePesos } from '../express/lib.js';

/** @typedef {import('@popcart/core/v1').RiderCash} RiderCash */
/** @typedef {import('@popcart/core/v1').OpsPayments} OpsPayments */
/** @typedef {import('@popcart/core/v1').PopCartApi} PopCartApi */

export const paymentKeys = { all: ['ops', 'payments'] };

/** Payments refresh every 30 s, like the rest of Ops. */
export const PAYMENTS_POLL_MS = 30_000;

/**
 * Over the limit first, then the most cash on hand, then by name.
 * @param {readonly RiderCash[]} riders
 */
export function sortRiderCash(riders) {
  return [...riders].sort((a, b) => Number(b.overLimit) - Number(a.overLimit) || b.onHandCentavos - a.onHandCentavos || a.name.localeCompare(b.name));
}

/**
 * The cash table's footer, and what "over the limit" counts.
 * @param {readonly RiderCash[]} riders
 */
export function cashTotals(riders) {
  return riders.reduce(
    (t, r) => ({
      collectedCentavos: t.collectedCentavos + r.collectedCentavos,
      remittedCentavos: t.remittedCentavos + r.remittedCentavos,
      onHandCentavos: t.onHandCentavos + r.onHandCentavos,
      overLimit: t.overLimit + (r.overLimit ? 1 : 0),
    }),
    { collectedCentavos: 0, remittedCentavos: 0, onHandCentavos: 0, overLimit: 0 },
  );
}

export const REMIT_NOTE_MAX = 200;

/**
 * "Record cash handed in": an amount in pesos, more than zero and no more than what the rider
 * holds; an optional note.
 * @param {{ amount: string; note: string }} form
 * @param {number} onHandCentavos
 * @returns {{ ok: true; value: { amountCentavos: number; note?: string } } | { ok: false; errors: { amount?: string; note?: string } }}
 */
export function validateRemittance(form, onHandCentavos) {
  /** @type {{ amount?: string; note?: string }} */
  const errors = {};
  const raw = String(form.amount ?? '').trim();
  const c = parsePesos(raw);
  if (!raw) errors.amount = 'Enter the cash handed in, in pesos.';
  else if (c === null) errors.amount = 'Pesos and centavos only, like 1500 or 1499.50';
  else if (c <= 0) errors.amount = 'More than ₱0.';
  else if (c > onHandCentavos) errors.amount = `That's more than the ${peso(onHandCentavos)} they hold.`;
  const note = String(form.note ?? '').trim();
  if (note.length > REMIT_NOTE_MAX) errors.note = `Keep it under ${REMIT_NOTE_MAX} characters.`;
  if (errors.amount || errors.note) return { ok: false, errors };
  return { ok: true, value: note ? { amountCentavos: /** @type {number} */ (c), note } : { amountCentavos: /** @type {number} */ (c) } };
}

/**
 * Validate, then POST /ops/riders/:userId/remittances.
 * @param {Pick<PopCartApi, 'addRemittance'>} api
 * @param {RiderCash} rider
 * @param {{ amount: string; note: string }} form
 */
export async function recordRemittance(api, rider, form) {
  const r = validateRemittance(form, rider.onHandCentavos);
  if (!r.ok) return r;
  const remittance = await api.addRemittance(rider.riderUserId, r.value);
  return { ok: /** @type {const} */ (true), remittance };
}

/**
 * The rider row once a remittance lands (the next poll confirms it).
 * @param {RiderCash} rider
 * @param {{ amountCentavos: number; createdAt: string }} remittance
 * @returns {RiderCash}
 */
export function applyRemittance(rider, remittance) {
  const remitted = rider.remittedCentavos + remittance.amountCentavos;
  const onHand = cashOnHand(rider.collectedCentavos, remitted);
  return { ...rider, remittedCentavos: remitted, onHandCentavos: onHand, overLimit: overCashLimit(onHand, rider.cashLimitCentavos), lastRemittedAt: remittance.createdAt };
}

/** "Pay now pending" / "Payment failed". @param {'pending' | 'failed'} status */
export function ewalletStatus(status) {
  return status === 'failed' ? { label: 'Failed', tone: /** @type {const} */ ('error') } : { label: 'Waiting to pay', tone: /** @type {const} */ ('warning') };
}

/** @type {Record<string, { label: string; tone: 'success' | 'warning' | 'error' | 'info' | 'neutral' }>} */
const OUTCOMES = {
  applied: { label: 'Applied', tone: 'success' },
  duplicate: { label: 'Duplicate', tone: 'neutral' },
  ignored_status: { label: 'Ignored', tone: 'neutral' },
  already_paid: { label: 'Already paid', tone: 'warning' },
  unknown_order: { label: 'Unknown order', tone: 'error' },
  amount_mismatch: { label: 'Amount mismatch', tone: 'error' },
  currency_mismatch: { label: 'Currency mismatch', tone: 'error' },
  not_reserved: { label: 'Order not open', tone: 'error' },
};

/**
 * The payment log's outcome pill (payment_events.outcome, migration 0001). Refused events
 * (mismatch, unknown, not open) are red: money may need to go back.
 * @param {string} outcome
 */
export function eventOutcome(outcome) {
  const o = String(outcome || '');
  return OUTCOMES[o] ?? { label: o.replace(/_/g, ' ') || '—', tone: /** @type {const} */ ('info') };
}

/** "GCASH" etc. for a provider id. @param {string} provider */
export function providerLabel(provider) {
  return String(provider || '').toUpperCase() || '—';
}
